import { CHART_BAR_GREEN, chartAxisMaximum, formatChartDate, formatChartBarValue } from './chartFormatting';
import { displayDigits } from '../../utils/displayDigits';
import { useTranslation } from 'react-i18next';
/**
 * Bar Chart
 * Vertical gradient bars with a dashed average line and axis labels.
 * Used by: fitness report, nutrition (calories), periodic (TSS), power profile (duration).
 */
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useChartScrub } from '../../hooks/useChartScrub';
import { svgEl, gridLine } from './svgUtils';
import type { WeekBar, BarUnit } from './types';

interface BarMargins { mx: number; mr: number }

const MT = 10;
const MB = 24;
const Y_AXIS_W = 40;
const LABEL_MUTED = 'var(--fg-2, #64748b)';

interface BarLayout {
  mx: number;
  mr: number;
  cw: number;
  ch: number;
  maxVal: number;
  avg: number;
  nb: number;
  bw: number;
  gapX: number;
  xLabelStep: number;
  bars: Array<{ bx: number; by: number; bh: number; cx: number }>;
}

interface RenderOptions {
  compactLabels: boolean;
  htmlYLabels: boolean;
  hideXLabels: boolean;
}

/** Show at most ~8 x-axis labels to avoid overlap on dense weekly data. */
function xLabelStep(nb: number): number {
  if (nb <= 10) return 1;
  if (nb <= 18) return 2;
  if (nb <= 30) return 4;
  return Math.max(1, Math.ceil(nb / 8));
}

function computeLayout(data: WeekBar[], VH: number, margins: BarMargins, VW: number): BarLayout | null {
  if (!data.length) return null;
  const MX = margins.mx;
  const MR = margins.mr;
  const cw = VW - MX - MR;
  const ch = VH - MT - MB;
  const vals = data.map((d) => d.value);
  const maxVal = chartAxisMaximum(vals);
  const avg = vals.reduce((s, v) => s + v, 0) / vals.length;
  const nb = data.length;
  const bw = Math.max(1, Math.floor((cw / nb) * 0.58));
  const gapX = cw / nb - bw;
  const step = Math.max(xLabelStep(nb), Math.ceil(nb / 4));

  const bars = data.map((d, i) => {
    const bx = MX + (cw / nb) * i + gapX / 2;
    const bh = (d.value / maxVal) * ch;
    const by = MT + ch - bh;
    return { bx, by, bh: Math.max(bh, 1), cx: bx + bw / 2 };
  });

  return { mx: MX, mr: MR, cw, ch, maxVal, avg, nb, bw, gapX, xLabelStep: step, bars };
}

const formatValue = formatChartBarValue;
const formatAxisValue = formatChartBarValue;

function chartLabel(
  svg: SVGSVGElement,
  x: number,
  y: number,
  text: string,
  anchor: 'start' | 'middle' | 'end',
  fontSize: number,
  fill = LABEL_MUTED,
  weight = 600,
): void {
  const t = svgEl('text', {
    x,
    y,
    'text-anchor': anchor,
    'font-size': fontSize,
    'font-weight': weight,
    fill,
    'font-family': 'Vazir,Vazirmatn,Inter,sans-serif',
    'letter-spacing': '.02em',
  });
  t.textContent = displayDigits(text);
  svg.appendChild(t);
}

function render(
  svg: SVGSVGElement,
  data: WeekBar[],
  color: string,
  gradId: string,
  unit: BarUnit,
  VH: number,
  VW: number,
  margins: BarMargins,
  hoverIdx: number | null,
  opts: RenderOptions,
): void {
  svg.innerHTML = '';
  const layout = computeLayout(data, VH, margins, VW);
  if (!layout) return;

  const { mx: MX, mr: MR, ch, maxVal, avg, bw, bars, xLabelStep: step } = layout;
  const yLabelSize = opts.compactLabels ? 9 : 10;
  const xLabelSize = 11;
  const defs = svgEl('defs');
  const gr = svgEl('linearGradient', { class: 'chart-bar-gradient', id: gradId, x1: 0, y1: 0, x2: 0, y2: 1 });
  const s1 = svgEl('stop', { offset: '0%' }); s1.setAttribute('stop-color', color);
  const s2 = svgEl('stop', { offset: '100%' }); s2.setAttribute('stop-color', 'var(--chart-bar-base)'); s2.setAttribute('stop-opacity', '0.85');
  gr.appendChild(s1); gr.appendChild(s2); defs.appendChild(gr); svg.appendChild(defs);

  for (let i = 0; i <= 3; i++) {
    const y = MT + (ch / 3) * i;
    gridLine(svg, MX, y, VW - MR, y);
    if (!opts.htmlYLabels) {
      chartLabel(svg, MX - 6, y + 3.5, formatAxisValue(maxVal * (1 - i / 3), unit), 'end', yLabelSize);
    }
  }

  const ay = MT + (1 - avg / maxVal) * ch;
  svg.appendChild(svgEl('line', { x1: MX, y1: ay, x2: VW - MR, y2: ay, stroke: 'rgba(255,255,255,.22)', 'stroke-width': 1, 'stroke-dasharray': '4 5' }));

  data.forEach((d, i) => {
    const { bx, by, bh } = bars[i];
    const isHover = hoverIdx === i;
    svg.appendChild(svgEl('rect', {
      x: bx,
      y: by,
      width: bw,
      height: bh,
      rx: Math.min(5, bw / 2),
      fill: `url(#${gradId})`,
      opacity: hoverIdx == null || isHover ? 1 : 0.45,
      stroke: 'var(--chart-bar-outline)',
      'stroke-width': isHover ? 1.5 : .6,
    }));
    if (!opts.hideXLabels && (i === data.length - 1 || (i % step === 0 && (data.length - 1 - i >= Math.max(1, step - 1))))) {
      chartLabel(svg, bx + bw / 2, VH - 6, formatChartDate(d.label), i === 0 ? 'start' : i === data.length - 1 ? 'end' : 'middle', xLabelSize);
    }
  });
}

function clientXToViewBoxX(svg: SVGSVGElement | null, clientX: number): number | null {
  if (!svg) return null;
  const ctm = svg.getScreenCTM();
  if (!ctm) return null;
  const pt = svg.createSVGPoint();
  pt.x = clientX;
  pt.y = 0;
  return pt.matrixTransform(ctm.inverse()).x;
}

function viewBoxPointToWrap(
  svg: SVGSVGElement | null,
  wrap: HTMLDivElement | null,
  x: number,
  y: number,
): { left: number; top: number } | null {
  if (!svg || !wrap) return null;
  const ctm = svg.getScreenCTM();
  if (!ctm) return null;
  const pt = svg.createSVGPoint();
  pt.x = x;
  pt.y = y;
  const screen = pt.matrixTransform(ctm);
  const wrapRect = wrap.getBoundingClientRect();
  return { left: screen.x - wrapRect.left, top: screen.y - wrapRect.top };
}

function barIndexAtX(xVB: number, layout: BarLayout): number | null {
  for (let i = 0; i < layout.bars.length; i++) {
    const { bx } = layout.bars[i];
    if (xVB >= bx && xVB <= bx + layout.bw) return i;
  }
  let nearest = 0;
  let best = Math.abs(xVB - layout.bars[0].cx);
  for (let i = 1; i < layout.bars.length; i++) {
    const dist = Math.abs(xVB - layout.bars[i].cx);
    if (dist < best) {
      best = dist;
      nearest = i;
    }
  }
  return nearest;
}

interface HoverTip {
  left: number;
  top: number;
  date: string;
  value: string;
}

interface YTick {
  value: string;
  topPct: number;
}

export interface ChartBarsProps {
  data: WeekBar[];
  color: string;
  unit: BarUnit;
  height?: number;
  /** Fill the available card body while retaining height as a minimum. */
  fillHeight?: boolean;
  flush?: boolean;
  /** Zero horizontal plot padding (Monthly Hours and similar dense bar charts). */
  tight?: boolean;
  interactive?: boolean;
  style?: React.CSSProperties;
}

/** Plot starts near left edge — Y ticks rendered as HTML beside the chart. */
const FLUSH_MARGINS: BarMargins = { mx: 6, mr: 6 };
const TIGHT_FLUSH_MARGINS: BarMargins = { mx: 0, mr: 0 };

export function ChartBars({
  data,
  color: _color,
  unit,
  height: minimumHeight = 168,
  fillHeight = false,
  flush = false,
  tight = false,
  interactive = false,
  style,
}: ChartBarsProps) {
  const { i18n } = useTranslation();
  const color = CHART_BAR_GREEN;
  const hostRef = useRef<HTMLDivElement>(null);
  const [availableHeight, setAvailableHeight] = useState(minimumHeight);
  const [availableWidth, setAvailableWidth] = useState(360);
  const height = fillHeight ? Math.max(minimumHeight, availableHeight) : minimumHeight;
  useEffect(() => {
    if (!hostRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      setAvailableHeight(Math.round(entry.contentRect.height));
      setAvailableWidth(Math.round(entry.contentRect.width));
    });
    observer.observe(hostRef.current);
    return () => observer.disconnect();
  }, []);
  const svgRef = useRef<SVGSVGElement>(null);
  const plotRef = useRef<HTMLDivElement>(null);
  const gradId = `bars_${useId().replace(/:/g, '')}`;
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [hoverTip, setHoverTip] = useState<HoverTip | null>(null);
  const margins = tight ? TIGHT_FLUSH_MARGINS : FLUSH_MARGINS;
  const yAxisW = unit === 'time' ? 105 : unit === 'km' ? 94 : Y_AXIS_W;
  const width = Math.max(120, availableWidth - yAxisW);
  const htmlYLabels = true;
  const hideXLabels = false;

  const layout = useMemo(() => computeLayout(data, height, margins, width), [data, height, width, margins.mx, margins.mr]);

  const renderOpts: RenderOptions = useMemo(
    () => ({ compactLabels: flush, htmlYLabels, hideXLabels }),
    [flush, htmlYLabels, hideXLabels],
  );

  const yTicks: YTick[] = useMemo(() => {
    if (!layout || !htmlYLabels) return [];
    return [0, 1, 2, 3].map((i) => ({
      value: formatAxisValue(layout.maxVal * (1 - i / 3), unit),
      topPct: ((MT + (layout.ch / 3) * i) / height) * 100,
    }));
  }, [layout, htmlYLabels, unit, height, i18n.language]);

  useEffect(() => {
    if (svgRef.current) {
      render(svgRef.current, data, color, gradId, unit, height, width, margins, interactive ? hoverIdx : null, renderOpts);
    }
  }, [data, color, gradId, unit, height, width, margins.mx, margins.mr, hoverIdx, interactive, renderOpts, i18n.language]);

  const updateHover = useCallback((idx: number | null) => {
    setHoverIdx(idx);
    if (idx == null || !layout) {
      setHoverTip(null);
      return;
    }
    const bar = layout.bars[idx];
    const pos = viewBoxPointToWrap(svgRef.current, plotRef.current, bar.cx, bar.by);
    if (!pos) {
      setHoverTip(null);
      return;
    }
    setHoverTip({
      left: pos.left,
      top: pos.top - 4,
      date: formatChartDate(data[idx].label),
      value: formatValue(data[idx].value, unit),
    });
  }, [layout, data, unit]);

  const handleScrub = useCallback((clientX: number) => {
    if (!interactive || !layout) return;
    const xVB = clientXToViewBoxX(svgRef.current, clientX);
    if (xVB == null || xVB < layout.mx || xVB > width - layout.mr) {
      updateHover(null);
      return;
    }
    updateHover(barIndexAtX(xVB, layout));
  }, [interactive, layout, width, updateHover]);

  const scrub = useChartScrub(interactive, handleScrub, () => updateHover(null));

  return (
    <div
      className="chart-touch-plot"
      data-no-pull
      ref={hostRef}
      style={{
        display: 'flex',
        alignItems: 'stretch',
        flex: fillHeight ? '1 0 auto' : undefined,
        minHeight: minimumHeight,
        height: fillHeight ? undefined : height,
        minWidth: 0,
        userSelect: 'none',
        cursor: interactive ? 'crosshair' : undefined,
        ...style,
      }}
      {...scrub}
    >
      {htmlYLabels && (
        <div
          style={{
            position: 'relative',
            width: yAxisW,
            flexShrink: 0,
            height: fillHeight ? undefined : height,
            pointerEvents: 'none',
          }}
        >
          {yTicks.map((tick) => (
            <span
              key={tick.value}
              dir="ltr"
              style={{
                position: 'absolute',
                right: 2,
                top: `${tick.topPct}%`,
                transform: 'translateY(-50%)',
                fontSize: 12,
                fontWeight: 600,
                color: LABEL_MUTED,
                lineHeight: 1.8,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {displayDigits(tick.value)}
            </span>
          ))}
        </div>
      )}

      <div ref={plotRef} style={{ position: 'relative', flex: 1, minWidth: 0 }}>
        {interactive && hoverTip && (
          <div
            style={{
              position: 'absolute',
              left: hoverTip.left,
              top: hoverTip.top,
              transform: 'translate(-50%, -100%)',
              pointerEvents: 'none',
              textAlign: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 600, color: LABEL_MUTED, lineHeight: 1.3 }}>
              {displayDigits(hoverTip.date)}
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg-1)', lineHeight: 1.3 }}>
              {displayDigits(hoverTip.value)}
            </div>
          </div>
        )}
        <svg
          ref={svgRef}
          className="ltr-island"
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio={flush ? 'none' : undefined}
          style={{ width: '100%', height: '100%', position: 'absolute', inset: 0, display: 'block' }}
        />
      </div>
    </div>
  );
}
